// This program has been developed by students from the bachelor Computer Science at
// Utrecht University within the Software Project course.
// Copyright (c) Utrecht University (Department of Information and Computing Sciences)

import { generateFallOffMap } from "./Falloff";
import { dilate, erode } from "./Morphology";
import { getCollapsedNoise } from "./Noise";
import { levelData } from "@/utils/Types";
import seedRandom from "seedrandom";

/**
 * Mix to number arrays together according to the mixing strength
 * @param a Array
 * @param b Array
 * @param mix Mix
 * @returns Mix
 */
function mix(a: number[][], b: number[][], mix: number): number[][] {
  //Check if both arrays are of the same size
  if (!(a.length == b.length && a[0].length == b[0].length))
    throw new Error("Incompatible sizes");
  const width = a.length;
  const height = a[0].length;

  const res: number[][] = [];
  for (let x = 0; x < width; x++) {
    res[x] = [];
    for (let y = 0; y < height; y++) {
      res[x][y] = clamp(a[x][y] * (1 - mix) + b[x][y] * mix);
    }
  }
  return res;
}

/**
 * Divide the image in 0 and 1 by using a threshold
 * @param img Binary image
 * @param threshold Threshold
 * @returns Division
 */
function makeBinary(img: number[][], threshold: number): number[][] {
  const width = img.length;
  const height = img[0].length;
  const res: number[][] = [];
  for (let x = 0; x < width; x++) {
    res[x] = [];
    for (let y = 0; y < height; y++) {
      res[x][y] = img[x][y] < threshold ? 0 : 1;
    }
  }
  return res;
}

/**
 * Combines 2 number arrays and clamps them to [0..1]
 * @param a Array
 * @param b Array
 * @returns Combination
 */
function combine(a: number[][], b: number[][]): number[][] {
  if (!(a.length == b.length && a[0].length == b[0].length))
    throw new Error("Incompatible sizes");

  const res: number[][] = [];
  const width = a.length;
  const height = a[0].length;

  for (let x = 0; x < width; x++) {
    res[x] = [];
    for (let y = 0; y < height; y++) {
      res[x][y] = clamp(a[x][y] + b[x][y]);
    }
  }

  return res;
}

/**
 * Clamp a value between 0 and 1
 * @param val To be clamped value
 * @returns Clamped value
 */
function clamp(val: number): number {
  return Math.min(Math.max(val, 0), 1);
}

/**
 * Create the constraint map from the level data
 * @param level Level layout
 * @param buffer Buffer
 * @returns Constraint map
 */
function createConstrainMap(level: levelData, buffer: number): number[][] {
  const res: number[][] = [];
  for (let x = 0; x < level.size.width + 2 * buffer; x++) {
    res[x] = [];
    for (let y = 0; y < level.size.height + 2 * buffer; y++) {
      res[x][y] = 0;
    }
  }

  //Start and end
  for (let i = 0; i < level.start.length; i++) {
    res[level.start[i].x + buffer][level.start[i].y + buffer] = 1;
  }
  for (let i = 0; i < level.end.length; i++) {
    res[level.end[i].x + buffer][level.end[i].y + buffer] = 1;
  }

  //Paths
  for (let i = 0; i < level.path.length; i++) {
    res[level.path[i].x + buffer][level.path[i].y + buffer] = 1;
  }

  //Interactables
  for (let i = 0; i < level.interactables.length; i++) {
    res[level.interactables[i].x + buffer][
      level.interactables[i].y + buffer
    ] = 1;
  }
  //Obstacles
  for (let i = 0; i < level.obstacles.length; i++) {
    res[level.obstacles[i].x + buffer][level.obstacles[i].y + buffer] = 1;
  }
  //Entity paths
  if (level.entities) {
    for (let i = 0; i < level.entities.length; i++) {
      res[level.entities[i].x][level.entities[i].y] = 1;
      for (let j = 0; j < level.entities[i].waypoints.length; j++) {
        res[level.entities[i].waypoints[j].x + buffer][
          level.entities[i].waypoints[j].y + buffer
        ] = 1;
      }
    }
  }
  return res;
}

/**
 * Mask source with the mask
 * @param source To be masked source
 * @param mask Mask
 * @returns Masked source
 */
function mask(source: number[][], mask: number[][]): number[][] {
  const res: number[][] = [];
  for (let x = 0; x < source.length; x++) {
    res[x] = [];
    for (let y = 0; y < source[0].length; y++) {
      res[x][y] = source[x][y] * mask[x][y];
    }
  }
  return res;
}

/**
 * Generate a world map based on constraints
 * @param leveldata Level layout
 * @param lacunarity Lacunarity number
 * @param persistence Persistance number
 * @param octaves Octaves number
 * @param dilationStrength Dilation strength
 * @param erosionStrength Erosion strength
 * @param buffer Buffer
 * @returns Constraint check
 */
function generateWorld(
  leveldata: levelData,
  lacunarity: number,
  persistence: number,
  octaves: number,
  dilationStrength: number,
  erosionStrength: number,
  buffer: number
): number[][] {
  //Set seeding
  seedRandom(leveldata.seed.toString(), { global: true });

  const falloff = generateFallOffMap(
    leveldata.size.width + 2 * buffer,
    leveldata.size.height + 2 * buffer
  );

  const constraintMap = createConstrainMap(leveldata, buffer);

  //Noise that describes the shape of the terrain
  const terrainNoise = getCollapsedNoise(
    leveldata.seed,
    leveldata.size.width + 2 * buffer,
    leveldata.size.height + 2 * buffer,
    lacunarity,
    persistence,
    octaves
  );

  //Ensure that the land mass is an island
  const islandMap = makeBinary(mix(falloff, terrainNoise, 0.25), 0.4);

  //Combine the constraints with the current island
  const combinedConstraints = combine(islandMap, constraintMap);

  const dilation = dilate(combinedConstraints, dilationStrength);
  const erosion = erode(dilation, erosionStrength);

  //Make sure that all the constraints are still supported by land
  const constraintCheck = combine(erosion, constraintMap);

  return constraintCheck;
}

/**
 * Get all the possible location where you can place decorations
 * @param terrain Terrain
 * @param level Level
 * @param buffer Buffer
 * @returns Possible location
 */
function getDecorationSafeZone(
  terrain: number[][],
  level: levelData,
  buffer: number
): number[][] {
  const constraints = createConstrainMap(level, buffer);
  const res: number[][] = [];
  for (let x = 0; x < terrain.length; x++) {
    res[x] = [];
    for (let y = 0; y < terrain[0].length; y++) {
      res[x][y] = terrain[x][y] ^ constraints[x][y];
    }
  }
  return res;
}

export {
  mix,
  makeBinary,
  combine,
  clamp,
  generateWorld,
  getDecorationSafeZone,
  mask,
};
